import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { TextField, Button, Box, Typography, Grid, Divider, InputAdornment, IconButton } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import logo from '../../assetts/images/RUNMYBOT_logo.svg';
import cornerImage from '../../assetts/images/Corner_image.svg';
import LinkedInIcon from '../../assetts/images/linkedin.svg';
import GoogleIcon from '../../assetts/images/google.svg';
import microsoftLogo from '../../assetts/images/microsoft-icon.svg';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import './Login.css';
import { useDispatch, useSelector } from 'react-redux';
import { loginRequest, socialMediaLoginRequest, socialMediaLoginCallback, loginFailure } from '../../redux/actions/authActions';
import { getMethodOptions, use2FAHandlers } from './LoginHelper.js';
import { OtpInputs, MethodCard, PrimaryButton, BackButton, MethodBadge, OtpError } from './TwoFactorComponents';
import { TWO_FA_REQUIRED } from '../../redux/actions/authActions';

const Login = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, error } = useSelector((state) => state.auth);
  const userType = useSelector((state) => state.auth.userDetails?.user_type);
  const twoFARequired = useSelector((state) => state.auth.twoFARequired);
  const tempToken = useSelector((state) => state.auth.tempToken);
  const availableMethods = useSelector((state) => state.auth.availableMethods);
  const twoFAEmail = useSelector((state) => state.auth.twoFAEmail) || '';

  const SIGNUP_ENABLED = process.env.REACT_APP_SIGNUP_ENABLED === 'true';
  const SOCIAL_MEDIA_LOGIN_ENABLED = process.env.REACT_APP_SOCIAL_MEDIA_LOGIN_ENABLED === 'true';

  // Login form state
  const [formFields, setFormFields] = useState({ email: '', password: '' });
  const [placeholderText, setPlaceholderText] = useState('Email');
  const [placeholderTextPassword, setPlaceholderTextPassword] = useState('Password');
  const [showPassword, setShowPassword] = useState(false);
  const [redirected, setRedirected] = useState(false);

  // 2FA state
  const [activeStep, setActiveStep] = useState(0);
  const [method, setMethod] = useState('email');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [totpStep, setTotpStep] = useState('loading');
  const otpRefs = useRef([]);
  const prevTempToken = useRef(null);
  const otpFilled = otp.every((d) => d !== '');
  const { handleSendCode, handleOtpChange, handleOtpKeyDown, handleOtpPaste, handleVerify, handleResend } = use2FAHandlers({
    otp, setOtp, setOtpError, setTimer, setActiveStep, setLoading, otpRefs, formFields, dispatch, loginRequest, method, setQrCodeUrl, setTotpStep, tempToken, totpStep
  });


  useEffect(() => {
    if (isAuthenticated && !redirected && userType) {
      setRedirected(true);
      (userType === 2 || userType === 3)
        ? navigate('/partner-dashboard')
        : navigate('/choicescreen', { state: { view: 'Activities' } });
    }
  }, [isAuthenticated, redirected, userType]);

  useEffect(() => {
    const code = new URLSearchParams(location.search).get('code');
    if (code) dispatch(socialMediaLoginCallback('google', window.location.origin, code));
  }, [location, dispatch]);

  useEffect(() => {
    if (timer <= 0) return;
    const id = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [timer]);

  useEffect(() => {
    if (twoFARequired && tempToken && tempToken !== prevTempToken.current) {
      prevTempToken.current = tempToken;
      setActiveStep(1);
    }
  }, [twoFARequired, tempToken]);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key !== 'Enter') return;

      if (activeStep === 1 && !loading) {
        handleSendCode();
      }

      if (activeStep === 2) {
        if (otpFilled && !loading) {
          handleVerify();
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [activeStep, otpFilled, loading, handleSendCode, handleVerify]);

  const handleLogin = (e) => {
    e.preventDefault();
    const { email, password } = formFields;
    if (!email?.trim() && !password?.trim()) return dispatch(loginFailure('Email and Password are required.'));
    if (!email?.trim()) return dispatch(loginFailure('Email is required.'));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return dispatch(loginFailure('Please enter a valid email address.'));
    if (!password?.trim()) return dispatch(loginFailure('Password is required.'));
    const credentials = {
      email: formFields.email,
      password: formFields.password,
    };
    // Dispatch login action
    dispatch({ type: TWO_FA_REQUIRED, payload: { twoFARequired: false } });
    dispatch(loginRequest(credentials));
    // setActiveStep(1)
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && activeStep === 0) handleLogin(e); };

  const handleBackToMethod = () => {
    setActiveStep(1); setOtp(['', '', '', '', '', '']); setOtpError(''); setTotpStep('loading');
  };
  const handleBackToLogin = () => {
    setActiveStep(0);
  };

  const socialLogin = (provider) => dispatch(socialMediaLoginRequest({ provider, currentUrl: window.location.origin }));

  const renderStep0 = () => (
    <>
      <TextField
        label={placeholderText === "" ? "Email" : ""}
        placeholder={placeholderText}
        value={formFields.email}
        variant="outlined" fullWidth required margin="normal" className='customTextField'
        onFocus={() => setPlaceholderText('')}
        onChange={(e) => { setPlaceholderText(e.target.value ? '' : 'Email'); setFormFields({ ...formFields, email: e.target.value }); }}
        onBlur={() => { if (!formFields.email) setPlaceholderText('Email'); }}
        InputProps={{ startAdornment: <InputAdornment position="start"><Person2OutlinedIcon /></InputAdornment> }}
      />
      <TextField
        label={placeholderTextPassword === "" ? "Password" : ""}
        placeholder={placeholderTextPassword}
        type={showPassword ? 'text' : 'password'} variant="outlined" fullWidth margin="normal"
        value={formFields.password} className='customTextField'
        InputLabelProps={{ shrink: true }}
        onFocus={() => setPlaceholderTextPassword('')}
        onChange={(e) => { setPlaceholderTextPassword(e.target.value ? '' : 'Password'); setFormFields({ ...formFields, password: e.target.value }); }}
        onBlur={() => { if (!formFields.password) setPlaceholderTextPassword('Password'); }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><LockOutlinedIcon /></InputAdornment>,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)}
                onMouseDown={(e) => e.preventDefault()} edge="end" className="password-toggle">
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Button variant="contained" className='loginbutton' fullWidth onClick={handleLogin} sx={{ mt: 2, boxShadow: 'none' }}>
        Login
      </Button>

      <div className="login-links-row">
        <div className="login-links-row__left">
          {SIGNUP_ENABLED && <>
            <Typography variant="body2" className='text-style'>Don't have an account? &nbsp;</Typography>
            <Typography variant="body2" className="text-style">
              <Link to="/signup" className="link-pink">Sign up</Link>
            </Typography>
          </>}
        </div>
        <Typography variant="body2" className='text-style'>
          <Link to="/forgot-password" className="link-pink">Forgot Password?</Link>
        </Typography>
      </div>

      {error && <p className="login-error">{error}</p>}

      {SOCIAL_MEDIA_LOGIN_ENABLED && <>
        <Divider className="login-divider">or <b>Login</b> with Others</Divider>
        {[
          { src: GoogleIcon, alt: 'Google', label: 'Login with Google', provider: 'google' },
          { src: LinkedInIcon, alt: 'LinkedIn', label: 'Login with LinkedIn', provider: 'linkedin' },
          { src: microsoftLogo, alt: 'Microsoft', label: 'Login with Microsoft', provider: 'microsoft' },
        ].map(({ src, alt, label, provider }) => (
          <Button key={label} variant="outlined" fullWidth onClick={() => socialLogin(provider)} className="social-btn">
            <Box component="img" src={src} alt={alt} className="social-btn__icon" />
            {label}
          </Button>
        ))}
      </>}
    </>
  );

  const renderStep1 = () => (
    <>
      <Typography variant="h6" className="step-title">Verify it's you</Typography>
      <Typography variant="body2" className="step-subtitle">
        Choose how you'd like to receive your one-time verification code.
      </Typography>

      {getMethodOptions(availableMethods, twoFAEmail).map((opt) => (
        <MethodCard key={opt.value} {...opt} selected={method === opt.value} onSelect={setMethod} />
      ))}

      <PrimaryButton onClick={handleSendCode} disabled={loading} onKeyDown={(e) => e.key === "Enter" && handleSendCode()} className="mt-2">
        {loading ? 'Sending...' : `Send code via ${method === 'sms' ? 'SMS' : method === 'email' ? 'Email' : 'Authenticator'}`}
      </PrimaryButton>
      <BackButton onClick={handleBackToLogin} label="← Back to login" />
    </>
  );

  const renderStep2 = () => (
    <>
      {/* <MethodBadge method={method} /> */}

      {method === 'totp' && totpStep === 'qr' && (
        <>
          <Typography variant="h6" className="step-title">Set up Authenticator</Typography>
          <Typography variant="body2" className="step-subtitle">
            Scan this QR code with any authenticator app such as <strong>Google Authenticator</strong>, <strong>Authy</strong> or <strong>iPhone Passwords</strong>
          </Typography>
          <div className="qr-container">
            {qrCodeUrl
              ? <QRCodeSVG value={qrCodeUrl} size={180} />
              : <p className="qr-loading">Loading...</p>
            }
          </div>
          <Typography variant="body2" className="otp-scan-hint">
            After scanning, enter the 6-digit code from your app:
          </Typography>
          <OtpInputs otp={otp} otpError={otpError} otpRefs={otpRefs} onChange={handleOtpChange} onKeyDown={handleOtpKeyDown} onPaste={handleOtpPaste} />
          <OtpError message={otpError} />
          <PrimaryButton onClick={handleVerify} onKeyDown={(e) => e.key === "Enter" && handleVerify()} disabled={!otpFilled || loading} className="mt-1">
            {loading ? 'Verifying...' : 'Confirm & Enable'}
          </PrimaryButton>
        </>
      )}


      {(method !== 'totp' || totpStep === 'verify') && (
        <>
          <Typography variant="h6" className="step-title">
            {method === 'totp' ? 'Enter authenticator code' : method === 'email' ? 'Check your inbox' : 'Check your phone'}
          </Typography>
          <Typography variant="body2" className="step-subtitle">
            {method === 'totp'
              ? <>Open your authenticator app and enter the <strong className="text-dark">6-digit code</strong>.</>
              : <>We sent a code to <strong className="text-dark">{method === 'sms' ? '+1 •••• ••• 42' : 'al••@gmail.com'}</strong>. Enter it below.</>
            }
          </Typography>
          <OtpInputs otp={otp} otpError={otpError} otpRefs={otpRefs} onChange={handleOtpChange} onKeyDown={handleOtpKeyDown} onPaste={handleOtpPaste} />
          <OtpError message={otpError} />
          <PrimaryButton onClick={handleVerify} disabled={!otpFilled || loading}>
            {loading ? 'Verifying...' : 'Verify & Continue'}
          </PrimaryButton>
          {method !== 'totp' && (
            <div className="resend-row">
              <span className="resend-row__text">Didn't receive it?</span>
              <Button variant="text" onClick={handleResend} disabled={timer > 0} className="resend-btn">
                {timer > 0 ? `Resend in ${timer}s` : 'Resend code'}
              </Button>
            </div>
          )}
        </>
      )}
      <BackButton onClick={handleBackToMethod} label="← Change method" />
    </>
  );

  const renderStep3 = () => (
    <div className="success-container">
      <div className="success-icon">
        <CheckCircleOutlineIcon className="success-icon__svg" />
      </div>
      <Typography variant="h6" className="success-title">You're in!</Typography>
      <Typography variant="body2" className="success-subtitle">Identity verified. Redirecting to your dashboard...</Typography>
    </div>
  );

  return (
    <div className="login-page">
      <div className="login-footer">
        <p><span className='rmb_run_p_log'>RUN</span><span className='rmb_my_p_log'>MY</span><span className='rmb_bot_p_log'>BOT®</span></p>&nbsp;All Rights Reserved {new Date().getFullYear()}.
      </div>

      <Grid container className="login-grid" onKeyDown={handleKeyDown}>
        {/* Left panel */}
        <Grid item xs={12} md={6} container direction="column" justifyContent="center" alignItems="center" spacing={0} padding={10}
          style={{ position: 'relative' }}
        >
          <div className="login-corner-img">
            <img src={cornerImage} alt="Login" />
          </div>
          <div className="login-form-box">
            <div className="login-logo">
              <img src={logo} alt="Logo" />
            </div>

            {activeStep === 0 && renderStep0()}
            {activeStep === 1 && renderStep1()}
            {activeStep === 2 && renderStep2()}
            {activeStep === 3 && renderStep3()}
          </div>
        </Grid>

        {/* Right panel */}
        <Grid item xs={12} md={6} className="login-side-image" />
      </Grid>
    </div>
  );
};

export default Login;